import { BrowserModule } from '@angular/platform-browser';
import { NgModule, enableProdMode, isDevMode, ErrorHandler } from '@angular/core';
import { ActionReducer, StoreModule, MetaReducer, Store } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ToastrModule } from 'ngx-toastr';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { StoreRouterConnectingModule, RouterStateSerializer } from '@ngrx/router-store';
import { storeLogger } from 'ngrx-store-logger';
import { CommonModule, DatePipe, DecimalPipe, CurrencyPipe } from '@angular/common';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ColorSwatchesModule } from 'ngx-color/swatches';
import { ClipboardModule } from 'ngx-clipboard';

import { USER_PROVIDED_META_REDUCERS } from '@ngrx/store';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import {
  GridAllModule,
  ColumnChooserService, ExcelExportService, FilterService,
  ResizeService, SortService,
  ToolbarService, GridComponent, PageService, EditService, CommandColumnService
} from '@syncfusion/ej2-angular-grids';
import { NgSelectModule } from '@ng-select/ng-select';
import * as fromContainers from './containers';
import * as fromComponents from './components';
import * as fromPipes from './pipes';

import * as fromStateManagement from 'src/app/state-management';

// Import trackingFactory separately to prevent circular dependency
import { trackingFactory } from 'src/app/services/tracking/tracking.service';
import * as fromServices from './services';


import {
  DnvFeatureService,
  DnvGisUtility,
  DnvUiModule,
} from 'dnv-lib';
import { environment } from 'src/environments/environment';
import { NgxGalleryModule } from '@vinlos/ngx-gallery';


import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
// import { MsalService, MSAL_INSTANCE, MsalGuard, MsalInterceptor } from './msal';
// import { IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { ReactiveFormsModule } from '@angular/forms';

// import { MsalService, MSAL_INSTANCE, MsalGuard, MsalInterceptor, MsalBroadcastService } from './msal';
// import { IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
// import { MSAL_GUARD_CONFIG, InteractionType, MSAL_INTERCEPTOR_CONFIG } from './msal/constants';
// import { MsalGuardConfiguration } from './msal/msal.guard.config';
// import { MsalInterceptorConfig } from './msal/msal.interceptor.config';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { FormsModule } from '@angular/forms';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';

import { IPublicClientApplication, PublicClientApplication, BrowserCacheLocation, LogLevel, InteractionType } from '@azure/msal-browser';
import {
  MsalBroadcastService, MsalGuard, MsalGuardConfiguration,
  MsalInterceptor, MsalInterceptorConfiguration, MsalModule,
  MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG
} from '@azure/msal-angular';
import { httpInterceptorProviders, ArcGisTokenGuard, AppSettingsGuard } from './http-interceptors';
import { SetAppSettings } from 'src/app/state-management';
import { RouterModule, Routes } from '@angular/router';


export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.auth.clientId,
      authority: environment.auth.authority,
      redirectUri: environment.auth.redirectUri,
      postLogoutRedirectUri: environment.auth.logoutUri,
      navigateToLoginRequestUrl: true,  // allow redirect to the link you came from if you were asked to login.
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: false
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  protectedResourceMap.set(environment.webApiUrl, ['openid']);
  protectedResourceMap.set('api', ['openid']);
  protectedResourceMap.set('arcgis', ['openid']);
  protectedResourceMap.set(environment.webApiUrl + '/arcgis/*', ['openid']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read']
    }
  };
}

declare global {
  interface Window {
    environment: string | null;
  }
}

const routes: Routes = [
  { path: 'loading', component: fromContainers.LoadingComponent, canActivate: [MsalGuard] },
  { path: 'logout', component: fromContainers.LogoutComponent },
  { path: 'map', component: fromContainers.MapViewComponent, canActivate: [ArcGisTokenGuard, MsalGuard, AppSettingsGuard] },
  { path: 'wmrp', component: fromContainers.MapViewComponent, canActivate: [ArcGisTokenGuard, MsalGuard, AppSettingsGuard] },
  { path: 'wmrp/:analysisId', component: fromContainers.MapViewComponent, canActivate: [ArcGisTokenGuard, MsalGuard, AppSettingsGuard] },
  { path: 'wmrp/:analysisId/:scheduleId', component: fromContainers.MapViewComponent, canActivate: [ArcGisTokenGuard, MsalGuard, AppSettingsGuard] },
  { path: 'rap', component: fromContainers.RapViewComponent, canActivate: [ArcGisTokenGuard, MsalGuard, AppSettingsGuard] },
  { path: 'engdrawings', component: fromContainers.EngDrawingsComponent, canActivate: [MsalGuard] },
  { path: 'postingplans', component: fromContainers.PostingPlansComponent, canActivate: [MsalGuard] },
  { path: 'serviceconnections', component: fromContainers.ServiceConnectionComponent, canActivate: [MsalGuard, AppSettingsGuard] },
  { path: 'edreports', component: fromContainers.DnvEDReportsComponent, canActivate: [MsalGuard, AppSettingsGuard] },
  { path: '**', redirectTo: '/loading' }
];

export function log(reducer: ActionReducer<any>): ActionReducer<any> {
  if (window.environment === 'Development') {
    return storeLogger()(reducer);
  } else {
    return storeLogger(window.appConfig.loggingOption)(reducer);
  }
}

export function appEnvironment(value: string): boolean {
  return value === (window.environment || '');
}
if (appEnvironment('production')) {
  enableProdMode();
}
else {
  isDevMode();
}

export function getMetaReducers(httpClient: HttpClient): MetaReducer<any>[] {
  return [log, trackingFactory(httpClient)];
}


@NgModule({
  declarations: [
    AppComponent,
    ...fromContainers.containers,
    ...fromComponents.components,
    ...fromPipes.pipes,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    CommonModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      // disableTimeOut: true, // Set to true for errors so that they stay visible
      positionClass: 'toast-bottom-right',
      preventDuplicates: false,
    }),
    RouterModule.forRoot(routes, {
      useHash: false,
      anchorScrolling: 'enabled',
      onSameUrlNavigation: 'reload',
      enableTracing: true,
      scrollPositionRestoration: 'enabled'
    }),
    StoreModule.forRoot(fromStateManagement.reducers),
    StoreRouterConnectingModule.forRoot(),
    EffectsModule.forRoot([
      ...fromStateManagement.effects
    ]),
    NgxGalleryModule,
    DnvUiModule,
    ReactiveFormsModule,
    NgbModule,
    ColorSwatchesModule,
    NgxSliderModule,
    FormsModule,
    ClipboardModule,
    NgSelectModule,
    PdfJsViewerModule,
    GridAllModule,
    AppRoutingModule],
  providers: [
    ...httpInterceptorProviders,
    ArcGisTokenGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    ...fromServices.services,
    ...fromPipes.pipes,
    {
      provide: USER_PROVIDED_META_REDUCERS,
      deps: [HttpClient],
      useFactory: getMetaReducers
    },
    ResizeService,
    FilterService,
    SortService,
    PageService,
    ToolbarService,
    ColumnChooserService,
    ExcelExportService,
    EditService,
    CommandColumnService,
    DatePipe, DecimalPipe, CurrencyPipe,
    DnvFeatureService,
    DnvGisUtility,
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule { }
